import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { storageRuleService } from "/app/src/services";

import { Search } from "/app/src/components/generic/components/search";

import { useTranslation } from "react-i18next";
import { StorageRule as StorageRuleType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
/**
 * StorageRules component that renders storage rules data.
 *
 * @returns {JSX.Element} The rendered storage rules component.
 */
export default function StorageRules() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const columnHelper = createColumnHelper<StorageRuleType>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("description", {
        id: "description",
        cell: (info) => info.getValue(),
        header: () => t("translation:description"),
      }),
      columnHelper.accessor("requiredCapacity", {
        id: "requiredCapacity",
        cell: (info) => info.getValue(),
        header: () => t("translation:capacity"),
      }),
    ],
    [columnHelper, t],
  );

  const goToStorageRule = useCallback(
    (storageRuleId: number) => {
      navigate(`/explorer/storage_rules/${storageRuleId}`);
    },
    [navigate],
  );

  const {
    data: storageRules,
    count: storageRulesCount,
    isFetching,
  } = usePaginatedData<StorageRuleType>({
    queryKey: ["storage_rules"],
    searchString,
    sort,
    page,
    pageSize,
    service: storageRuleService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: storageRulesCount,
    isFetching,
    page,
    setPage,
    setPageSize,
    pageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:storage_rules")} - ItemPath</title>
      </Helmet>
      <Search allowClear setSearchString={setSearchString} />
      <Table
        loading={isFetching}
        rows={storageRules}
        tableColumns={columns}
        length={storageRulesCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        rowClicked={goToStorageRule}
        emptyText={t("translation:no_storage_rules")}
      />
    </div>
  );
}
